import jsPDF from 'jspdf';
import { getCallTypeLabel, formatUserName } from '../../utils/industryConfig';

export function exportEvaluationToPDF(evaluation, formatDate) {
  // Validation handled by caller (EvaluationList.js) with toast notification
  if (!evaluation.evaluation) {
    return;
  }

  const evalData = evaluation.evaluation;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 15;
  const maxWidth = pageWidth - (margin * 2);
  let yPos = margin;

  // Helper to add new page if needed
  const checkNewPage = (requiredSpace = 15) => {
    if (yPos + requiredSpace > 280) {
      pdf.addPage();
      yPos = margin;
    }
  };

  // Helper to add wrapped text
  const addText = (text, fontSize = 10, style = 'normal', color = [0, 0, 0]) => {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', style);
    pdf.setTextColor(...color);
    const lines = pdf.splitTextToSize(text, maxWidth);
    lines.forEach(line => {
      checkNewPage();
      pdf.text(line, margin, yPos);
      yPos += fontSize * 0.5;
    });
    yPos += 3;
  };

  // Title
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(37, 99, 235); // Blue
  pdf.text('Call Evaluation Report', margin, yPos);
  yPos += 10;

  // Metadata
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 0, 0);
  const displayName = (evaluation.salespersonName && evaluation.salespersonName !== evaluation.salesperson)
    ? evaluation.salespersonName
    : formatUserName({ email: evaluation.salesperson });
  addText(`Salesperson: ${displayName}`);
  addText(`Prospect: ${evaluation.prospect}`);
  addText(`Call Type: ${getCallTypeLabel(evaluation.callType)}`);
  addText(`Date: ${formatDate(evaluation.uploadedAt)}`);
  addText(`Uploaded By: ${evaluation.uploadedByEmail || 'N/A'}`);
  yPos += 5;

  // Overall Score (centered, large)
  checkNewPage(25);
  pdf.setFillColor(239, 246, 255); // Light blue
  pdf.rect(margin, yPos, maxWidth, 25, 'F');
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(37, 99, 235);
  pdf.text(`${evalData.overallScore}/100`, pageWidth / 2, yPos + 15, { align: 'center' });
  pdf.setFontSize(10);
  pdf.text('Overall Sales Call Score', pageWidth / 2, yPos + 22, { align: 'center' });
  yPos += 30;

  // Final Verdict
  if (evalData.finalVerdict) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('Final Verdict', margin, yPos);
    yPos += 7;
    addText(evalData.finalVerdict, 10, 'normal');
  }

  // Talking Ratio
  if (evalData.talkingRatio) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('Talking Ratio', margin, yPos);
    yPos += 7;
    addText(`Prospect: ${evalData.talkingRatio.prospect || 'N/A'}`);
    addText(`Salesperson: ${evalData.talkingRatio.salesperson || 'N/A'}`);
  }

  // Category Breakdown
  if (evalData.categoryScores && evalData.categoryScores.length > 0) {
    checkNewPage(15);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('Category Breakdown', margin, yPos);
    yPos += 7;

    evalData.categoryScores.forEach(cat => {
      checkNewPage(12);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      const scoreDisplay = cat.score === null || cat.score === undefined 
        ? 'N/A' 
        : `${cat.score}/${cat.maxScore || 10}`;
      pdf.text(`${cat.category || 'Unknown'}: ${scoreDisplay}`, margin, yPos);
      yPos += 5;
      if (cat.feedback) {
        addText(cat.feedback, 9, 'normal', [75, 85, 99]);
      }
      yPos += 2;
    });
  }

  // Structured Evaluation
  if (evalData.evaluation) {
    const sections = [
      { title: 'What They Did Well', data: evalData.evaluation.didWell, icon: 'PASS' },
      { title: 'Minor Improvements', data: evalData.evaluation.minorImprovements, icon: 'WARN' },
      { title: 'Major Mistakes', data: evalData.evaluation.majorMistakes, icon: 'FAIL' },
      { title: 'Lost Emotional Clarity', data: evalData.evaluation.lostConnection, icon: 'NOTE' }
    ];

    sections.forEach(section => {
      if (section.data && section.data.length > 0) {
        checkNewPage();
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 58, 138);
        pdf.text(`${section.title}`, margin, yPos);
        yPos += 6;
        section.data.forEach((item, idx) => {
          addText(`${idx + 1}. ${item}`, 9, 'normal');
        });
      }
    });
  }

  // Strategy Checklist
  if (evalData.strategyChecklist && evalData.strategyChecklist.length > 0) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('Strategy Checklist', margin, yPos);
    yPos += 7;
    evalData.strategyChecklist.forEach(item => {
      checkNewPage();
      const symbol = item.passed ? '[X]' : '[ ]';
      addText(`${symbol} ${item.item}`, 9, 'normal', item.passed ? [5, 150, 105] : [220, 38, 38]);
    });
  }

  // Key Tactics
  if (evalData.keyTactics && evalData.keyTactics.length > 0) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('Top 3 Key Tactics Observed', margin, yPos);
    yPos += 7;
    evalData.keyTactics.forEach((tactic, idx) => {
      addText(`${idx + 1}. ${tactic}`, 9, 'normal');
    });
  }

  // Unforced Errors
  if (evalData.unforcedErrors && evalData.unforcedErrors.length > 0) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('Top 3 Unforced Errors', margin, yPos);
    yPos += 7;
    evalData.unforcedErrors.forEach((error, idx) => {
      addText(`${idx + 1}. ${error}`, 9, 'normal');
    });
  }

  // Prospect Takeaways
  if (evalData.prospectTakeaways) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('Prospect Takeaways', margin, yPos);
    yPos += 7;
    if (evalData.prospectTakeaways.coreProblem) addText(`Core Problem: ${evalData.prospectTakeaways.coreProblem}`, 9);
    if (evalData.prospectTakeaways.motivation) addText(`Motivation: ${evalData.prospectTakeaways.motivation}`, 9);
    if (evalData.prospectTakeaways.emotions) addText(`Emotions: ${evalData.prospectTakeaways.emotions}`, 9);
    if (evalData.prospectTakeaways.timeline) addText(`Timeline: ${evalData.prospectTakeaways.timeline}`, 9);
  }

  // Missed Opportunities
  if (evalData.missedOpportunities && evalData.missedOpportunities.length > 0) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('3 Missed Opportunities', margin, yPos);
    yPos += 7;
    evalData.missedOpportunities.forEach((opp, idx) => {
      addText(`${idx + 1}. ${opp}`, 9, 'normal');
    });
  }

  // Tactical Next Steps
  if (evalData.tacticalNextSteps && evalData.tacticalNextSteps.length > 0) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('3 Tactical Next Steps', margin, yPos);
    yPos += 7;
    evalData.tacticalNextSteps.forEach((step, idx) => {
      addText(`${idx + 1}. ${step}`, 9, 'normal');
    });
  }

  // Final Learning
  if (evalData.finalLearning) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('Final Learning', margin, yPos);
    yPos += 7;
    addText(evalData.finalLearning, 9, 'normal');
  }

  // Transcript (abbreviated if too long)
  if (evaluation.transcript && evaluation.transcript.text) {
    checkNewPage();
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text('Transcript', margin, yPos);
    yPos += 7;
    
    // Format transcript with speaker labels
    const transcriptText = evaluation.transcript.text.substring(0, 5000);
    const lines = transcriptText.split('\n');
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;
      
      checkNewPage(8);
      
      // Check if line starts with SALESPERSON: or PROSPECT:
      if (trimmedLine.startsWith('SALESPERSON:')) {
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(37, 99, 235); // Blue for salesperson
        pdf.text('SALESPERSON:', margin, yPos);
        yPos += 4;
        
        const text = trimmedLine.substring(13).trim();
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        const wrappedLines = pdf.splitTextToSize(text, maxWidth - 5);
        wrappedLines.forEach(wrappedLine => {
          checkNewPage();
          pdf.text(wrappedLine, margin + 5, yPos);
          yPos += 4;
        });
        yPos += 2;
      } else if (trimmedLine.startsWith('PROSPECT:')) {
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(16, 185, 129); // Green for prospect
        pdf.text('PROSPECT:', margin, yPos);
        yPos += 4;
        
        const text = trimmedLine.substring(9).trim();
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        const wrappedLines = pdf.splitTextToSize(text, maxWidth - 5);
        wrappedLines.forEach(wrappedLine => {
          checkNewPage();
          pdf.text(wrappedLine, margin + 5, yPos);
          yPos += 4;
        });
        yPos += 2;
      } else {
        // Regular line without speaker label
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        const wrappedLines = pdf.splitTextToSize(trimmedLine, maxWidth);
        wrappedLines.forEach(wrappedLine => {
          checkNewPage();
          pdf.text(wrappedLine, margin, yPos);
          yPos += 4;
        });
      }
    });
    
    if (evaluation.transcript.text.length > 5000) {
      yPos += 3;
      addText('(Transcript truncated for PDF export)', 8, 'italic', [150, 150, 150]);
    }
  }

  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(`Generated from TreeGenie on ${new Date().toLocaleDateString()}`, pageWidth / 2, 287, { align: 'center' });

  // Save the PDF
  const fileName = `evaluation-${displayName.replace(/[^a-z0-9]/gi, '_')}-${evaluation.prospect.replace(/[^a-z0-9]/gi, '_')}-${new Date(evaluation.uploadedAt.toDate ? evaluation.uploadedAt.toDate() : evaluation.uploadedAt).toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);

  console.log(`✅ Exported evaluation: ${fileName}`);
}

